import React, {
  MouseEvent,
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from "react";
import useIsVisible from "components/hooks/useIsVisible";
import {
  useArchiveEmails,
  useGetEmailThread,
  useMarkEmailsUnreadStatus,
  useUnmarkSenderAsSpam,
} from "services/email";
import Loading from "components/common/Loading";
import { EmailMessage } from "protogen/conversation_pb";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  IconButton,
  Paper,
  Typography,
  Divider,
  Button,
  Tooltip,
  IconButtonProps,
} from "@mui/material";
import DateDisplay from "../common/DateDisplay";
import WithDividers from "components/helpers/WithDividers";
import EditIcon from "@mui/icons-material/Edit";
import EmailCompose from "./EmailCompose";
import EmailLetter from "components/email/EmailLetter";
import GridPage from "components/layout/GridPage";
import useIsMobile from "components/hooks/useIsMobile";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import { CalendarEvent } from "protogen/calendar_pb";
import { nowSec } from "../../types/calendars";
import { eventSubtitle } from "components/calendar/utils";
import ViewEventDialog from "components/calendar/ViewEventDialog";
import EditEventDialog from "components/calendar/EditEventDialog";
import { Suggestion, SuggestionActionsType } from "../activity/Suggestions";
import SuggestionActions, {
  SuggestionActionsHandle,
} from "../activity/SuggestionActions";
import InboxThreadHeader from "../inbox/InboxThreadHeader";
import {
  Bookmark,
  Forward,
  Reply,
  Trash2,
  Printer,
  Sparkles,
  Calendar,
  CheckCircle,
  FileText,
  ShieldOff,
} from "lucide-react";
import { styled } from "@mui/system";
import Collapse from "@mui/material/Collapse";
import BookmarkToTaskDialog, { fromEmail } from "../tasks/BookmarkToTaskDialog";
import LinkRouter from "../navigation/LinkRouter";
import HighlightProvider from "../context/HighlightProvider";
import { ReactComponent as MarkEmailUnread } from "../../icons/MarkEmailUnread.svg";
import { useNavigate } from "react-router";
import NotFound from "../NotFound";
import useTitle from "../hooks/useTitle";
import { StatusContext } from "../context/StatusContextProvider";
import Autoreplies from "components/common/Autoreplies";
import EmailLabels from "./EmailLabels";
import MarkSpamDialog from "./MarkSpamDialog";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import EmailInboxLabel from "./EmailInboxLabel";
import EmailAgentDisplay from "./EmailAgentDisplay";
import PopperMenu from "../common/PopperMenu";
import { MenuItem } from "@mui/material";
import { useStartAgent } from "services/agents";

const StyledIconButton = styled(IconButton)(() => ({
  padding: "8px",
  borderRadius: "100px",
  border: "2px solid #EAEBEC",
  background: "#FFF",
}));
const StyledAction = ({
  title,
  children,
  disabled,
  onClick,
}: { title: string } & IconButtonProps) => {
  return (
    <Tooltip title={title} placement="top" enterDelay={700}>
      <StyledIconButton disabled={disabled} onClick={onClick}>
        {children}
      </StyledIconButton>
    </Tooltip>
  );
};

const MagicButton = ({
  email,
  refresh,
  noFamily,
  agentAlreadyRan,
}: {
  email: EmailMessage;
  refresh: () => void;
  noFamily: boolean;
  agentAlreadyRan: boolean;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { request: startAgentRequest, loading: agentLoading } = useStartAgent();
  const disabled = noFamily || agentAlreadyRan || agentLoading;
  const handleMenuToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMenuClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpen(false);
  };

  const handleRunSchedulerAgent = async (
    agentType: "email-events" | "email-tasks" | "email-docs",
  ) => {
    setMenuOpen(false);
    if (disabled) {
      return;
    }
    await startAgentRequest({
      agentType: agentType,
      arguments: [
        {
          name: "emailRef",
          value: email.ref,
        },
      ],
      model: "",
    });
    refresh();
  };
  // We should also disable if the agents have already run.
  // const hasRun = email.events.length > 0;
  let tooltipTitle = "Run AI agent on this email";
  if (noFamily) {
    tooltipTitle = "Email must be assigned to a family before running an agent";
  } else if (agentAlreadyRan) {
    tooltipTitle = "Agent already executed on this email";
  }
  return (
    <Box>
      <Tooltip title={tooltipTitle} placement="top" enterDelay={700}>
        <StyledIconButton ref={anchorRef} onClick={handleMenuToggle}>
          <Sparkles
            height="20px"
            width="20px"
            stroke={disabled ? "#6B6E7B" : "#198282"}
          />
        </StyledIconButton>
      </Tooltip>
      <PopperMenu
        open={menuOpen}
        anchorRef={anchorRef}
        handleClose={handleMenuClose}
      >
        <MenuItem
          disabled={disabled}
          sx={{
            color: "#3D3D3D",
            padding: "8px 16px",
            minHeight: "inherit",
          }}
          onClick={() => handleRunSchedulerAgent("email-events")}
        >
          <Calendar height="16px" width="16px" style={{ marginRight: "8px" }} />
          Run scheduler agent
        </MenuItem>
        <MenuItem
          disabled={disabled}
          sx={{
            color: "#3D3D3D",
            padding: "8px 16px",
            minHeight: "inherit",
          }}
          onClick={() => handleRunSchedulerAgent("email-tasks")}
        >
          <CheckCircle
            height="16px"
            width="16px"
            style={{ marginRight: "8px" }}
          />
          Run task agent
        </MenuItem>
        <MenuItem
          disabled={disabled}
          sx={{
            color: "#3D3D3D",
            padding: "8px 16px",
            minHeight: "inherit",
          }}
          onClick={() => handleRunSchedulerAgent("email-docs")}
        >
          <FileText height="16px" width="16px" style={{ marginRight: "8px" }} />
          Run doc agent
        </MenuItem>
      </PopperMenu>
    </Box>
  );
};

const QuickActions = ({
  handleReply,
  handleForward,
  quickResponses,
  handleQuickResponse,
}: {
  handleReply: (e: MouseEvent<HTMLButtonElement>) => void;
  handleForward: (e: MouseEvent<HTMLButtonElement>) => void;
  quickResponses: string[];
  handleQuickResponse: (response: string) => void;
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
        <Button
          variant="outlined"
          sx={{
            color: "#6B6E7B",
            height: "40px",
            border: "2px solid #D4D4D4",
            padding: "8px 20px",
            "&:hover": {
              borderColor: "#6B6E7B",
            },
          }}
          endIcon={<Reply size={20} color="#6B6E7B" />}
          onClick={handleReply}
        >
          Reply
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: "#6B6E7B",
            height: "40px",
            border: "2px solid #D4D4D4",
            padding: "8px 20px",
            "&:hover": {
              borderColor: "#6B6E7B",
            },
          }}
          endIcon={<Forward size={20} color="#6B6E7B" />}
          onClick={handleForward}
        >
          Forward
        </Button>
      </Box>
      {quickResponses.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="bodyHeavy">Quick replies</Typography>
          <Autoreplies
            suggestions={quickResponses}
            onSelect={(response) => {
              handleQuickResponse(response);
            }}
            sx={{
              padding: "0px",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const BookmarkManager = ({
  familyRef,
  email,
  refresh,
}: {
  familyRef?: string;
  email: EmailMessage;
  refresh: () => void;
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  let actions = null;
  if (email.taskBookmarks.length > 0) {
    actions = (
      <Box display={"flex"} flexDirection="column">
        {email.taskBookmarks.map((bookmark) => (
          <Box key={bookmark.ref}>
            <IconButton onClick={() => setOpen(true)}>
              <Bookmark size={20} color="#198282" fill={"#198282"} />
            </IconButton>
            <Typography variant="bodySmall" display={"inline"}>
              Saved to{" "}
              <LinkRouter
                inline={true}
                sx={{ color: "#198282", fontWeight: 600 }}
                targetNew={!isMobile}
                to={`/tasks/${encodeURIComponent(bookmark.ref)}`}
              >
                "{bookmark.title}"
              </LinkRouter>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  } else if (!familyRef) {
    actions = (
      <Tooltip title="Assign this email to a family before bookmarking">
        <Box
          sx={{
            height: "28px",
            color: "#6B6E7B",
            marginLeft: "5px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            opacity: 0.5,
            cursor: "pointer",
          }}
        >
          <Bookmark size={20} color="#6B6E7B" />
          <Typography variant="bodyHeavy" display={"inline"}>
            Save to task
          </Typography>
        </Box>
      </Tooltip>
    );
  } else if (email.taskBookmarks.length === 0) {
    actions = (
      <Button
        variant="text"
        onClick={() => setOpen(true)}
        sx={{
          height: "unset",
          color: "#6B6E7B",
          marginLeft: "5px",
        }}
        startIcon={<Bookmark size={20} color="#6B6E7B" />}
      >
        Save to task
      </Button>
    );
  }

  return (
    <>
      {actions}
      {familyRef && (
        <BookmarkToTaskDialog
          familyRef={familyRef}
          open={open}
          onClose={() => {
            setOpen(false);
            refresh();
          }}
          entry={fromEmail(email)}
        />
      )}
    </>
  );
};

const EventPill = ({
  event,
  onClick,
}: {
  event: CalendarEvent;
  onClick: () => void;
}) => {
  const isMobile = useIsMobile();
  const now = nowSec();
  const isPast =
    (!event.endSec && now > event.startSec) ||
    (event.endSec && now > event.endSec);
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={"8px"}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        borderRadius: "12px",
        padding: isMobile ? "12px 16px" : "20px 24px",
        ...(isPast
          ? {
              border: "1px solid #EAEBEC",
              background: "#FAF9FA",
            }
          : {
              background: "#F3F7F6",
            }),
      }}
    >
      <Box display="flex" flexDirection="column" gap="4px" flex={1}>
        <Box
          sx={{
            color: "text.primary",
            fontSize: "16px",
            fontWeight: 700,
          }}
        >
          {event.title}
        </Box>
        <Box
          sx={{
            color: "text.secondary",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {eventSubtitle(event, true)}
        </Box>
      </Box>
    </Box>
  );
};

type EmailRowProps = {
  email: EmailMessage;
  initialExpanded: boolean;
  persistExpanded: boolean;
  onReply: (e: EmailMessage, isForward: boolean) => void;
  refresh: () => void;
  calendarEvents: Map<string, CalendarEvent>;
  selectEvent: (eventRef: string) => void;
  actions: SuggestionActionsType;
  showQuickActions: boolean;
  familyRef?: string;
  onDeselect: () => void;
  showSubject: boolean;
  onArchived?: () => void;
  onQuickResponse: (e: EmailMessage, response: string) => void;
  isSpamView?: boolean;
};
const EmailRow = ({
  email,
  initialExpanded,
  persistExpanded,
  onReply,
  refresh,
  calendarEvents,
  selectEvent,
  actions,
  showQuickActions,
  familyRef,
  showSubject,
  onArchived,
  onDeselect,
  onQuickResponse,
  isSpamView,
}: EmailRowProps) => {
  const isMobile = useIsMobile();
  const [hovering, setHovering] = useState(false);
  const [open, setOpen] = useState(initialExpanded);
  const [editing, setEditing] = useState(initialExpanded && email.isDraft);
  const confirmState = useConfirmationDialog();
  const { refresh: statusRefresh } = useContext(StatusContext);
  const [spamDialogOpen, setSpamDialogOpen] = useState(false);
  const { request: unreadRequest, loading: unreadLoading } =
    useMarkEmailsUnreadStatus();
  const { request: archiveRequest, loading: archiveLoading } =
    useArchiveEmails();
  const { request: unmarkSpamRequest, loading: unmarkSpamLoading } =
    useUnmarkSenderAsSpam();
  const loading = unreadLoading || archiveLoading;
  const handleUnmarkSpam = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!unmarkSpamLoading) {
      await unmarkSpamRequest({ emailRef: email.ref });
      onArchived?.();
      refresh();
    }
  };
  const handleReply = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onReply(email, false);
  };
  const handleForward = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onReply(email, true);
  };

  const handleQuickResponse = async (response: string) => {
    onQuickResponse(email, response);
  };
  // We memoize the initial draft being passed into the editor since we don't want the editor
  // to re-render on every refresh.  All further state is handled internally in the editor,
  // including update network calls to the server.
  const initialDraft = useMemo(() => email, []);
  const quickResponses = useMemo(
    () => email.threadSuggestions?.quickResponses,
    [email],
  );
  useEffect(() => {
    if (initialExpanded) {
      setOpen(true);
    }
  }, [initialExpanded]);
  const markUnread = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!loading) {
      await unreadRequest({ emailRefs: [email.ref], markAsRead: false });
      statusRefresh(); // Triggers a notification badging update.
      onDeselect();
    }
  };
  const archiveEmail = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!loading) {
      confirmState.openDialog(async () => {
        await archiveRequest({ emailRefs: [email.ref] });
        onArchived?.();
        refresh();
      });
    }
  };
  const handlePrint = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(email.html);
      printWindow.document.close();
      printWindow.focus();
      // Set the document title dynamically
      printWindow.document.title = email.subject;
      setTimeout(
        () => {
          printWindow.print();
          printWindow.close();
        },
        1000 + email.attachments.length * 500,
      ); // Wait for a moment for image attachments to load.
    }
  };
  const emailTime = (
    <DateDisplay
      date={new Date(Number(email.timestampSec) * 1000)}
      color="#475467"
      useRelativeAsPrimary={false}
      sx={{
        fontSize: "13px",
        fontStyle: "italic",
      }}
    />
  );
  const emailActions = (
    <Box
      display={"flex"}
      flexDirection={"row"}
      gap={"8px"}
      justifyContent={isMobile ? "space-between" : undefined}
      padding={isMobile ? "10px 50px 10px" : undefined}
    >
      {open && email.isDraft && !isMobile && (
        <EditIcon
          onClick={() => setEditing(true)}
          sx={{
            height: "20px",
            width: "20px",
            marginLeft: "10px",
            cursor: "pointer",
          }}
        />
      )}
      {open && !email.isDraft && (
        <>
          <MagicButton
            email={email}
            refresh={refresh}
            noFamily={!familyRef}
            agentAlreadyRan={!!email.agentThreadRef}
          />
          {!email.isArchived && (
            <StyledAction
              disabled={loading}
              onClick={archiveEmail}
              title={"Archive"}
            >
              <Trash2 height="20px" width="20px" stroke="#198282" />
            </StyledAction>
          )}
          {!email.isDraft &&
            (isSpamView ? (
              <StyledAction
                disabled={unmarkSpamLoading}
                onClick={handleUnmarkSpam}
                title={"Not spam"}
              >
                <ShieldOff height="20px" width="20px" stroke="#198282" />
              </StyledAction>
            ) : (
              <StyledAction
                onClick={(e) => {
                  e.stopPropagation();
                  setSpamDialogOpen(true);
                }}
                title={"Block sender"}
              >
                <ShieldOff height="20px" width="20px" stroke="#198282" />
              </StyledAction>
            ))}
          <StyledAction onClick={handlePrint} title={"Print"}>
            <Printer height="20px" width="20px" stroke="#198282" />
          </StyledAction>
          <StyledAction
            disabled={loading}
            onClick={markUnread}
            title={"Mark unread"}
          >
            <MarkEmailUnread
              style={{
                height: "20px",
                width: "20px",
              }}
            />
          </StyledAction>
          <StyledAction onClick={handleReply} title={"Reply"}>
            <Reply height="20px" width="20px" stroke="#198282" />
          </StyledAction>
          <StyledAction onClick={handleForward} title={"Forward"}>
            <Forward height="20px" width="20px" stroke="#198282" />
          </StyledAction>
        </>
      )}
    </Box>
  );

  // If editing...open edit area here.
  if (editing) {
    return (
      <Box
        sx={{
          padding: isMobile ? "0 20px" : "8px 20px 0",
          background: "#FFF",
          marginBottom: "24px",
          scrollMarginBottom: "20px",
          ...(hovering ? { backgroundColor: "#e8f4f8" } : {}),
          ...(isMobile
            ? {
                borderTop: "none",
              }
            : {
                border: "1px solid #D4D4D4",
                borderRadius: "12px",
              }),
        }}
      >
        <EmailCompose
          setHovering={setHovering}
          onClose={() => {
            setOpen(false);
            setEditing(false);
          }}
          minHeight={"200px"}
          onSent={() => {
            setEditing(false);
            refresh();
          }}
          onRemove={() => {
            setEditing(false);
            setOpen(false);
            onArchived?.();
          }}
          initialDraft={initialDraft}
          inlineView={true}
          refreshableChange={() => {
            refresh();
          }}
        />
      </Box>
    );
  }
  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: isMobile ? "0px 24px" : undefined,
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent={"space-between"}
        gap="15px"
        alignItems="start"
        sx={{
          ...(persistExpanded ? {} : { cursor: "pointer" }),
        }}
        onClick={() => !persistExpanded && setOpen((o) => !o)}
      >
        <Box
          display="flex"
          flexDirection="row"
          gap="12px"
          alignItems="start"
          flex={1}
          marginTop={!isMobile ? "9px" : undefined}
        >
          <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: "40px" }}>
            <Avatar sx={{ height: 40, width: 40, fontSize: "16px" }}>
              {email.senderDisplayName[0]}
            </Avatar>
          </Box>
          <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography variant="bodyHeavy" sx={{ opacity: ".8" }}>
                {email.isDraft && (
                  <span
                    style={{
                      color: email.isScheduled ? "#198282" : "#C14743",
                      marginRight: "4px",
                    }}
                  >
                    [{email.isScheduled ? "scheduled " : ""}draft]
                  </span>
                )}
                <Tooltip
                  title={
                    email.fromAddress !== email.senderDisplayName
                      ? email.fromAddress
                      : undefined
                  }
                >
                  <div style={{ userSelect: "text" }}>
                    {email.senderDisplayName}
                  </div>
                </Tooltip>
                {showSubject && (
                  <Typography variant="body" display={"inline"}>
                    {" "}
                    &middot; {email.subject}
                  </Typography>
                )}
              </Typography>
              {isMobile &&
                (email.isDraft ? (
                  <EditIcon
                    onClick={() => setEditing(true)}
                    sx={{
                      height: "20px",
                      width: "20px",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  emailTime
                ))}
            </Box>
            {!open && (
              <div
                style={{
                  wordBreak: "break-word",
                  overflow: "hidden",
                  maxHeight: "40px",
                  fontSize: "14px",
                }}
              >
                <EmailInboxLabel emailType={email.emailType} />
                {email.text}
              </div>
            )}
            {open && (
              <>
                <Typography variant="bodySmall" sx={{ color: "#616161" }}>
                  <EmailInboxLabel emailType={email.emailType} />
                  to {email.toAddresses.join(", ")}
                  {email.ccAddresses.length > 0 && (
                    <>, cc {email.ccAddresses.join(", ")}</>
                  )}
                  {email.bccAddresses.length > 0 && (
                    <>, bcc {email.bccAddresses.join(", ")}</>
                  )}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        {!isMobile && (
          <Box display="flex" flexDirection="column" alignItems="end" gap="6px">
            {emailTime}
            {emailActions}
          </Box>
        )}
      </Box>
      {/* Or non-standard subject */}
      <Collapse in={open}>
        {isMobile && !email.isDraft && <Box>{emailActions}</Box>}
        <Box
          sx={{
            marginTop: "10px",
            ...(!isMobile
              ? {
                  marginLeft: "55px", // Avatar width + Gap
                }
              : {}),
            cursor: email.isDraft ? "pointer" : undefined,
          }}
          onClick={email.isDraft ? () => setEditing(true) : undefined}
        >
          {email.eventRefs.map((ref) => {
            const event = calendarEvents.get(ref);
            if (!event) {
              return null;
            }
            return (
              <EventPill
                key={ref}
                event={event}
                onClick={() => selectEvent(event.ref)}
              />
            );
          })}
          {email.agentThreadRef && (
            <EmailAgentDisplay
              threadRef={email.agentThreadRef}
              familyRef={familyRef}
            />
          )}
          <HighlightProvider
            familyRef={familyRef}
            entityRef={email.ref}
            entityType={"email"}
          >
            <EmailLetter email={email} />
          </HighlightProvider>
          {email.threadSuggestions?.hasSuggestions && (
            <Box sx={{ marginTop: "16px" }}>
              <Suggestion
                suggestions={email.threadSuggestions}
                entityRef={email.ref}
                entityType="email"
                defaultOpen={true}
                actions={actions}
                familyRef={familyRef}
              />
            </Box>
          )}
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={"16px"}
            padding={"12px 0"}
            alignItems={"start"}
          >
            {!email.isDraft && (
              <BookmarkManager
                familyRef={familyRef}
                email={email}
                refresh={refresh}
              />
            )}
          </Box>
        </Box>
        {showQuickActions && (
          <QuickActions
            handleReply={handleReply}
            handleForward={handleForward}
            quickResponses={quickResponses}
            handleQuickResponse={handleQuickResponse}
          />
        )}
      </Collapse>
      <ConfirmationDialog
        title="Archive email"
        noIcon={true}
        content="Are you sure you want to archive this email?"
        {...confirmState.dialogProps}
      />
      <MarkSpamDialog
        open={spamDialogOpen}
        fromAddress={email.fromAddress}
        emailRef={email.ref}
        onClose={() => setSpamDialogOpen(false)}
        onSpamMarked={() => {
          onArchived?.();
          refresh();
        }}
      />
    </Paper>
  );
};

type ReplyState = {
  replyTo: EmailMessage | null;
  isForward: boolean;
};

type ThreadSectionProps = {
  onDeselect: () => void;
  initialEmailRef: string;
  emails: EmailMessage[];
  refresh: () => Promise<void>;
  calendarEvents: Map<string, CalendarEvent>;
  familyRef?: string;
  isSpamView?: boolean;
};

const ThreadSection = ({
  emails,
  refresh,
  initialEmailRef,
  calendarEvents,
  familyRef,
  onDeselect,
  isSpamView,
}: ThreadSectionProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const composeRef = useRef<HTMLDivElement>();
  const [emailState, setEmailState] = useState<ReplyState>({
    replyTo: null,
    isForward: false,
  });
  const actionsRef = useRef<SuggestionActionsHandle | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [quickResponse, setQuickResponse] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState("");
  useEffect(() => {
    if (emailState.isForward || emailState.replyTo) {
      setTimeout(() => {
        composeRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "start",
        });
      }, 100);
    }
  }, [emailState.isForward, emailState.replyTo]);
  useEffect(() => {
    setEmailState({
      replyTo: null,
      isForward: false,
    });
  }, [initialEmailRef]);

  // Handle new drafts/sent messages/deletions when the edit modal is closed.
  const onClose = async () => {
    await refresh();
    setEmailState({
      replyTo: null,
      isForward: false,
    });
  };
  const onEnableEdit = (eventRef: string) => {
    setSelectedEvent(eventRef);
    setViewModalOpen(false);
    setEditModalOpen(true);
  };
  const selectEvent = (eventRef: string) => {
    setViewModalOpen(true);
    setSelectedEvent(eventRef);
  };
  const onEdited = () => {
    refresh();
    setEditModalOpen(false);
  };
  const showSubject = (e: EmailMessage) => {
    const firstEmail = emails[0];
    if (firstEmail.ref === e.ref) return false;
    const cleanSubject = e.subject.replace(/^(RE:|re:|FWD:|fwd:)/i, "").trim();
    return cleanSubject !== firstEmail.subject.trim();
  };
  const lastEmail = emails.length > 0 ? emails[emails.length - 1] : undefined;
  return (
    <Box display="flex" flexDirection="column" gap="20px">
      <WithDividers>
        {emails.map((e, i) =>
          // Don't show the last email if it's a draft and we're replying to it already.
          (emailState.isForward || emailState.replyTo) &&
          i === emails.length - 1 &&
          e?.isDraft ? null : (
            <EmailRow
              key={e.ref}
              email={e}
              initialExpanded={
                initialEmailRef === e.ref || i === emails.length - 1
              }
              persistExpanded={i === emails.length - 1}
              onReply={(e, f) => {
                if (lastEmail?.isDraft) {
                  return;
                }
                setEmailState({
                  replyTo: e,
                  isForward: f,
                });
              }}
              onQuickResponse={(e, r) => {
                setQuickResponse(r);
                setEmailState({
                  replyTo: e,
                  isForward: false,
                });
              }}
              refresh={refresh}
              onDeselect={onDeselect}
              calendarEvents={calendarEvents}
              selectEvent={selectEvent}
              actions={{
                suggestTask: (t) => actionsRef.current?.suggestTask(t),
                suggestFact: (f) => actionsRef.current?.suggestFact(f),
                suggestEvent: (e) => actionsRef.current?.suggestEvent(e),
              }}
              showQuickActions={
                i === emails.length - 1 && !e.isDraft && !emailState.replyTo
              }
              familyRef={familyRef}
              showSubject={showSubject(e)}
              isSpamView={isSpamView}
              onArchived={() => {
                if (emails.length <= 1) {
                  navigate(-1);
                }
                refresh();
              }}
            />
          ),
        )}
      </WithDividers>
      {emailState.replyTo && (
        <>
          <Divider />
          <Box
            ref={composeRef}
            sx={{
              padding: isMobile ? "0 24px" : "8px 20px 0",
              background: "#FFF",
              marginBottom: "24px",
              scrollMarginBottom: "20px",
              ...(isMobile
                ? {
                    borderTop: "none",
                  }
                : {
                    border: "1px solid #D4D4D4",
                    borderRadius: "12px",
                  }),
            }}
          >
            <EmailCompose
              onClose={onClose}
              minHeight={"200px"}
              inReplyTo={emailState.replyTo}
              onSent={onClose}
              onRemove={() => {
                if (emails.length <= 1) {
                  navigate(-1);
                } else {
                  onClose();
                }
              }}
              isForward={emailState.isForward}
              inlineView={true}
              refreshableChange={() => {
                refresh();
              }}
              initialContent={quickResponse}
            />
          </Box>
        </>
      )}
      <ViewEventDialog
        open={viewModalOpen}
        onClose={(r) => {
          if (r) {
            refresh();
          }
          setViewModalOpen(false);
        }}
        primaryAction={onEnableEdit}
        eventRef={selectedEvent}
      />
      <EditEventDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onEdited={onEdited}
        eventRef={selectedEvent}
      />
      <SuggestionActions ref={actionsRef} familyRef={familyRef || ""} />
    </Box>
  );
};

interface EmailThreadProps {
  emailRef: string;
  onThreadUpdates: () => void;
  onDeselect: () => void;
  isSpamView?: boolean;
}

export default ({
  emailRef,
  onThreadUpdates,
  onDeselect,
  isSpamView,
}: EmailThreadProps) => {
  const { isVisible } = useIsVisible({});
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { refresh: statusRefresh } = useContext(StatusContext);
  const [calendarEvents, setCalendarEvents] = useState<
    Map<string, CalendarEvent>
  >(new Map([]));
  useTitle(emails?.length ? emails[0].subject : "Inbox / Email");
  const [cursor, setCursor] = useState<string>("");
  const { request, data, errorCode } = useGetEmailThread((r) => {
    // Don't thrash the state when it's just image URLs changing.
    setCursor(r.nextCursor);
    setEmails((preValue) => {
      if (preValue.length !== r.emails.length) {
        // If the conversation ref is different, reset and use new emails
        if (
          r.emails.length > 0 &&
          preValue.length > 0 &&
          r.emails[0].conversationRef !== preValue[0].conversationRef
        ) {
          return r.emails;
        }

        const newEmails = r.emails.filter((newEmail) => {
          return !preValue.some((prevEmail) => prevEmail.ref === newEmail.ref);
        });
        return [...newEmails, ...preValue];
      }
      const hasChanges = r.emails.some((newEmail, index) => {
        const prevEmail = preValue[index];
        return (
          !prevEmail ||
          prevEmail.ref !== newEmail.ref ||
          prevEmail.timestampSec !== newEmail.timestampSec ||
          prevEmail.agentThreadRef !== newEmail.agentThreadRef ||
          JSON.stringify(prevEmail.taskBookmarks) !==
            JSON.stringify(newEmail.taskBookmarks) ||
          JSON.stringify(prevEmail.threadSuggestions) !==
            JSON.stringify(newEmail.threadSuggestions)
        );
      });
      if (!hasChanges) {
        return preValue;
      } else {
        return r.emails;
      }
    });
    setCalendarEvents(new Map(r.calendarEvents.map((e) => [e.ref, e])));
    if (r?.readStatusUpdated) {
      statusRefresh();
    }
  });

  const loadMore = () => {
    request({ emailRef, markAsRead: false, cursor: cursor });
  };

  useEffect(() => {
    request({ emailRef, markAsRead: true, cursor: "" });
    const pushHandler = () => {
      if (isVisible) {
        request({ emailRef, markAsRead: false, cursor: "" });
      }
    };
    registerHandler("email/", pushHandler);
    return () => {
      unregisterHandler("email/", pushHandler);
    };
  }, [emailRef]);

  if (errorCode === 404) return <NotFound title={"Email not found"} />;
  if (!data) {
    return <Loading />;
  }

  if (!emails || emails.length === 0) {
    // Only happens when you delete a draft without other threads.
    return (
      <GridPage>
        <Alert severity="warning">
          <AlertTitle>Empty Thread</AlertTitle>
          There are no emails associated with this thread.
        </Alert>
      </GridPage>
    );
  }
  return (
    <Box
      sx={{ width: "100%", marginBottom: "24px" }}
      display="flex"
      flexDirection={"column"}
      gap={"20px"}
    >
      <InboxThreadHeader noPaddingBottom={true}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          gap="16px"
        >
          <Typography
            variant="h3"
            sx={{
              color: "#101828",
              // Optimize for email subjects at the moment.
              fontSize: "20px",
              fontWeight: 500,
              flexGrow: 1,
              minWidth: 0, // Allow shrinking
            }}
          >
            {emails[0].subject}
          </Typography>
          {emails[0] && !emails[0].isDraft && (
            <EmailLabels
              label={emails[0].folder || ""}
              emailRef={emails[0].ref}
              editable={true}
              onUpdate={() => {
                onThreadUpdates();
              }}
            />
          )}
        </Box>
      </InboxThreadHeader>
      {emails[0].topics.includes("Virus Risk") && (
        <Alert severity="error">
          <AlertTitle>Virus Risk</AlertTitle>
          This email was flagged by our email security system as potentially
          containing a virus. Exercise caution with any attachments.
        </Alert>
      )}
      {emails[0].topics.includes("Spam Risk") && (
        <Alert severity="warning">
          <AlertTitle>Spam Risk</AlertTitle>
          This email was flagged by our email security system as potential spam.
          Verify the sender before taking any action.
        </Alert>
      )}
      {cursor && (
        <Box>
          <Button
            onClick={loadMore}
            sx={{ height: "26px" }}
            size="small"
            variant="text"
          >
            Load more
          </Button>
        </Box>
      )}
      <Divider />
      <ThreadSection
        initialEmailRef={emailRef}
        emails={emails}
        refresh={async () => {
          await request({ emailRef, markAsRead: false, cursor: "" });
          onThreadUpdates();
        }}
        calendarEvents={calendarEvents}
        familyRef={data.familyRef || undefined}
        onDeselect={onDeselect}
        isSpamView={isSpamView}
      />
    </Box>
  );
};
